import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import NameFields from '../ui/NameFields';
import { Alert } from '../ui/Alert';
import { AddressComposite, SelectMenu, TypeaheadChips } from '../ui';
import Checkbox from '../ui/Checkbox';
import { customerAPI } from '../../services/api';
import useTelemetry from '../../hooks/useTelemetry';
import { useSettingsContext } from '../../hooks/useSettings';
import useSalespersonOptions from '../../hooks/useSalespersonOptions';
import { deactivatedChipOptions, storedSalespersons } from '../../utils/userStatus';
import { formatPhoneInput } from '../../utils/phone';
import { toServiceAddressShape } from '../../utils/customerAddress';
import { XMarkIcon, ChevronDownIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

// IDI-314 (2026-09-12): default_margin is a documented dual-format field —
// this form's input is percentage-scale (0-99, e.g. "35" for 35%, step=0.5)
// but some legacy customer records store it as a 0..1 decimal fraction (e.g.
// 0.15 for 15% — same heuristic as quote-builder/quotePrefill.js's
// normalizeMarginValue, kept as a local copy here rather than a cross-module
// import since the two need OPPOSITE output scales: that one returns a 0..1
// decimal for the quote engine, this one needs the 0..100 display scale).
// Un-normalized, the raw decimal (0.15) fails step=0.5 validation and the
// native <form> submit silently no-ops — no error, no toast, no network
// call — which is what IDI-314 ("adding a contact appears to do nothing")
// actually was: 8 of 38 live customers have a decimal-scale default_margin
// and are currently unsaveable in this drawer, for ANY edit, not just contacts.
function marginToDisplayScale(raw) {
  if (raw == null || raw === '') return '';
  const n = typeof raw === 'string' ? parseFloat(raw) : raw;
  if (!Number.isFinite(n) || n < 0) return '';
  // Stored as a decimal fraction (<=1) → scale up to a percentage for display.
  // Stored already as a percentage (>1, a legacy/inconsistent stray — all 9
  // live non-null values checked this session were decimal-scale, so this
  // branch is defensive, not the norm) → pass through unchanged.
  const pct = n > 0 && n <= 1 ? n * 100 : n;
  return String(pct);
}

// Inverse of marginToDisplayScale, for write-back. Always emits decimal
// scale (0..1) — the dominant convention in live data — so every save
// through this form converges existing records onto ONE consistent scale
// instead of perpetuating the percent/decimal split that caused this bug.
function marginFromDisplayScale(display) {
  if (display === '' || display == null) return undefined;
  const n = parseFloat(display);
  if (!Number.isFinite(n)) return undefined;
  return n / 100;
}

/**
 * AddCustomerDrawer — Customer create/edit side-sheet (REQ-WF-06)
 *
 * Jobber pattern: name + company + display-name checkbox
 * Multi-contact with primary selection
 * Multi-address + same-as-billing + primary
 *
 * ── PR-73 Commit 2 (2026-08-24) — is_job_site provenance ──────────────
 *
 * Multi-address now carries an ADDITIONAL designation: is_job_site.
 * Provenance note for anyone greping for R-71 or R-75:
 *
 *   R-71 and R-75 (register 08-19) originally ruled that the job-site
 *   address is stored at quote/job level ONLY, with no customer-record
 *   bit. Dispatch 431 §2 (operator supersession, 2026-08-24) reversed
 *   that: is_job_site moves ONTO customer.addresses[] as a customer-
 *   level designation, alongside job.job_site_address which remains the
 *   job-level snapshot.
 *
 *   Reason for the supersession is on record in dispatch 431. If a
 *   stakeholder asks "why is job-site on the customer record when
 *   R-75 said it wasn't," the answer is here.
 *
 *   Stakeholders were not told about the supersession at the time —
 *   flagged 🟠 for next UAT (dispatch 431 §2). Presentation (Reading C,
 *   checkbox + selector) is the operator's derivation, flagged §8.11;
 *   expect it to be corrected at UAT.
 *
 *   Twin: CustomerFormPanel.js. Both sites carry the same UI per PR-72
 *   §1.22 "fix the pattern, not one instance" discipline.
 *
 * Salesperson multiselect
 * Lead source from Settings (REQ-DEP-01 binding)
 * Lead/not-lead toggle
 * Edit mirrors add (same Drawer, pre-populated)
 */
export default function AddCustomerDrawer({ open, onClose, onSaved, customerTypes = [], editCustomer = null }) {
  const isEdit = !!editCustomer;
  // PR-14 (2026-07-22): seed payment_type/terms from tenant Quote Defaults so
  // a new-customer form starts pre-populated instead of blank.
  const { getOptions, quoteDefaults } = useSettingsContext();
  const { emit } = useTelemetry();
  const firstInputRef = useRef(null);

  // PR-14 (2026-07-22): default status = 'active' (was 'lead' — operators prefer
  // starting active; Lead Source field auto-hides unless status is 'lead').
  // Payment terms + type seed from tenant Quote Defaults if present.
  const emptyForm = {
    first_name: '',
    last_name: '',
    company: '',
    use_company_as_display: false,
    customer_type: '',
    status: 'active',
    contacts: [{ first_name: '', last_name: '', phone: '', email: '', is_primary: true }],
    // PR-73 Commit 2 (dispatch 431 §3): is_job_site defaults to false
    // — the primary is IMPLICITLY the job site while
    // job_site_same_as_primary is true. The bit only carries meaning
    // when the flag is false and the user has explicitly designated.
    addresses: [{ line1: '', line2: '', city: '', state: '', zip: '', is_primary: true, is_job_site: false, label: 'Primary' }],
    billing_same_as_primary: true,
    // PR-73 Commit 2 (dispatch 431 §3, Reading C).
    // See the checkbox handler and the docblock at
    // AddCustomerDrawer.job-site.test.js for provenance (R-71/R-75
    // superseded by dispatch 431 §2). Twin at CustomerFormPanel.js.
    job_site_same_as_primary: true,
    // §1 (2026-08-08): separate billing address when the checkbox is off.
    billing_address: null,
    payment_terms: quoteDefaults?.payment_terms || '',
    payment_type: quoteDefaults?.payment_type || '',
    default_margin: '',
    salesperson: [],
    lead_source: '',
  };

  const [form, setForm] = useState(emptyForm);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);


  // Load settings-bound options
  const leadSourceOptions = getOptions('lead_sources');
  const customerTypeOptions = getOptions('customer_types');
  // Iter-18 + follow-up (2026-07-22): salesperson typeahead via shared hook.
  // Replaces the duplicate state + useEffect + useMemo trio that used to live
  // here and in CustomerFormPanel.js. `enabled: open` preserves the original
  // optimization of not fetching users while the drawer is closed. Filter
  // now driven by `vocabSeeds.user_role.v1.permissions.can_sell` so
  // office_admin (which the old hardcoded allow-list silently missed) is
  // correctly included. See hooks/useSalespersonOptions.js.
  const { options: offeredSalespersonOptions, deactivatedIds } = useSalespersonOptions({ enabled: open });
  // IDI-681 D2: a deactivated salesperson already on the record isn't offered,
  // but keeps its chip by saved name (chip-only, with the Deactivated icon) and
  // Save writes back that saved {id, name} unchanged.
  const salespersonOptions = useMemo(() => [
    ...(offeredSalespersonOptions || []),
    ...deactivatedChipOptions(storedSalespersons(editCustomer), offeredSalespersonOptions, deactivatedIds),
  ], [offeredSalespersonOptions, deactivatedIds, editCustomer]);
  const paymentTermOptions = getOptions('payment_terms');
  // PR-3 Q2 alignment: source payment_type droplist from Settings (canonical
  // vocab in useSettings.DEFAULTS.payment_types — 5 values matching vocabSeeds
  // / seedDefaults). Replaces the inline check/credit_card/ach/cash array.
  const paymentTypeOptions = getOptions('payment_types');
  const contactRoleOptions = getOptions('contact_roles').filter(o => o.value !== 'primary');

  // Merge customerTypes prop with settings (backward compat)
  const typeOptions = customerTypeOptions.length > 0
    ? customerTypeOptions
    : customerTypes.map(t => ({ value: t.name || t.value, label: t.label || t.name }));

  // Focus first input when drawer opens
  useEffect(() => {
    if (open) {
      setTimeout(() => firstInputRef.current?.focus(), 200);
      if (editCustomer) {
        // Pre-populate for edit mode
        setForm({
          first_name: editCustomer.first_name || editCustomer.name?.split(' ')[0] || '',
          last_name: editCustomer.last_name || editCustomer.name?.split(' ').slice(1).join(' ') || '',
          company: editCustomer.company || '',
          use_company_as_display: editCustomer.use_company_as_display || false,
          customer_type: editCustomer.customer_type || editCustomer.type || '',
          status: editCustomer.status || 'active',
          contacts: editCustomer.contacts?.length > 0
            ? editCustomer.contacts.map(c => ({
                ...c,
                first_name: c.first_name || c.name?.split(' ')[0] || '',
                last_name: c.last_name || c.name?.split(' ').slice(1).join(' ') || '',
                phone: c.phone || '',
                email: c.email || '',
                is_primary: c.is_primary || c.isPrimary || false,
              }))
            : [{ first_name: '', last_name: '', phone: editCustomer.phone || '', email: editCustomer.email || '', is_primary: true }],
          addresses: editCustomer.addresses?.length > 0
            ? editCustomer.addresses
            : (editCustomer.address && (editCustomer.address.street || editCustomer.address.line1 || editCustomer.address.city || editCustomer.address.zip))
              // IDI-48 (2026-09-02): wire shape carries `street`/`city`/`state`/`zip`
              // on migrated customers, not `line1`. Same three-branch read as
              // GoldenCustomerCard.js:72-99. Without this, opening the Edit drawer on a
              // migrated customer showed blank fields; saving then blanked the record
              // silently.
              ? [{ line1: editCustomer.address.line1 || editCustomer.address.street || '',
                   line2: editCustomer.address.line2 || editCustomer.serviceAddress?.address2 || '',
                   city: editCustomer.address.city || '',
                   state: editCustomer.address.state || '',
                   zip: editCustomer.address.zip || '',
                   is_primary: true, is_job_site: false, label: 'Primary' }]
              : (editCustomer.serviceAddress && (editCustomer.serviceAddress.address1 || editCustomer.serviceAddress.city || editCustomer.serviceAddress.postal))
                ? [{ line1: editCustomer.serviceAddress.address1 || '',
                     line2: editCustomer.serviceAddress.address2 || '',
                     city: editCustomer.serviceAddress.city || '',
                     state: editCustomer.serviceAddress.state || '',
                     zip: editCustomer.serviceAddress.postal || '',
                     is_primary: true, is_job_site: false, label: 'Primary' }]
                : [{ line1: '', line2: '', city: '', state: '', zip: '', is_primary: true, is_job_site: false, label: 'Primary' }],
          billing_same_as_primary: editCustomer.billing_same_as_primary !== false,
          // PR-73 Commit 2 (dispatch 431 §3): hydrate is_job_site on
          // each address entry, plus the form-state flag.
          job_site_same_as_primary: editCustomer.job_site_same_as_primary !== false,
          // §1 (2026-08-08): hydrate separate billing address when the customer
          // has one saved. Empty shape if none, so the reveal has something to bind.
          billing_address: editCustomer.billing_same_as_primary === false && editCustomer.billing_address
            ? { line1: '', line2: '', city: '', state: '', zip: '', ...editCustomer.billing_address }
            : null,
          payment_terms: editCustomer.payment_terms || '',
          payment_type: editCustomer.payment_type || '',
          default_margin: marginToDisplayScale(editCustomer.default_margin ?? editCustomer.gross_margin ?? ''),
          // IDI-138: extract just the id from stored {id, name} entries —
          // TypeaheadChips's value prop needs a flat array of raw id strings
          // (matching options[].value), not the object shape now stored on
          // the record (see buildPayload in the submit handler below). Legacy
          // plain-string entries pass through unchanged (a string has no
          // `.id`, and IS itself the value TypeaheadChips needs).
          //
          // Code review fix: do NOT fall back to `sp.name` when `sp.id` is
          // missing — see CustomerFormPanel's identical fix for the full
          // rationale (falling back would silently reintroduce the
          // name-as-id bug for exactly this entry on the next save).
          salesperson: (Array.isArray(editCustomer.salespersons) ? editCustomer.salespersons
            : Array.isArray(editCustomer.salesperson) ? editCustomer.salesperson
            : editCustomer.salesperson ? [editCustomer.salesperson] : []
          ).map((sp) => (sp && typeof sp === 'object' ? (sp.id || '') : sp)).filter(Boolean),
          lead_source: editCustomer.lead_source ?? editCustomer.leadSource ?? '',
        });
        setShowAdvanced(true);
      } else {
        setForm(emptyForm);
        setShowAdvanced(false);
      }
      setError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editCustomer]);

  // Trap focus and handle Escape
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    setError(null);
  };

  // ─── Contact management ────────────────────────────────────────────────────
  const addContact = () => {
    setForm(prev => ({
      ...prev,
      contacts: [...prev.contacts, { first_name: '', last_name: '', phone: '', email: '', is_primary: false }],
    }));
  };

  const removeContact = (idx) => {
    setForm(prev => {
      const contacts = prev.contacts.filter((_, i) => i !== idx);
      // Ensure at least one primary
      if (contacts.length > 0 && !contacts.some(c => c.is_primary)) {
        contacts[0].is_primary = true;
      }
      return { ...prev, contacts };
    });
  };

  const updateContact = (idx, field, value) => {
    setForm(prev => {
      const contacts = [...prev.contacts];
      contacts[idx] = { ...contacts[idx], [field]: value };
      return { ...prev, contacts };
    });
  };

  const setPrimaryContact = (idx) => {
    setForm(prev => ({
      ...prev,
      contacts: prev.contacts.map((c, i) => ({ ...c, is_primary: i === idx })),
    }));
  };

  // ─── Address management ────────────────────────────────────────────────────
  const addAddress = () => {
    setForm(prev => ({
      ...prev,
      // PR-73 Commit 2: new entries default is_job_site: false.
      addresses: [...prev.addresses, { line1: '', line2: '', city: '', state: '', zip: '', is_primary: false, is_job_site: false, label: 'Additional' }],
    }));
  };

  const removeAddress = (idx) => {
    setForm(prev => {
      const addresses = prev.addresses.filter((_, i) => i !== idx);
      if (addresses.length > 0 && !addresses.some(a => a.is_primary)) {
        addresses[0].is_primary = true;
      }
      // PR-73 Commit 2 (dispatch 426 §3.1): promote-on-delete for
      // is_job_site mirrors the is_primary promotion above — BUT
      // only when the flag is off AND the resulting length is >= 2
      // (i.e. an explicit designation is still needed). At length=1,
      // is_job_site is implicit under the flag; we clear all bits
      // and restore the flag to true.
      const collapsed = addresses.length <= 1;
      if (collapsed) {
        return {
          ...prev,
          addresses: addresses.map(a => ({ ...a, is_job_site: false })),
          job_site_same_as_primary: true,
        };
      }
      // Length >= 2: if no entry carries is_job_site, promote index 0
      // (mirror the is_primary promotion pattern).
      if (!addresses.some(a => a.is_job_site) && !prev.job_site_same_as_primary) {
        addresses[0] = { ...addresses[0], is_job_site: true };
      }
      return { ...prev, addresses };
    });
  };

  const updateAddress = (idx, addr) => {
    setForm(prev => {
      const addresses = [...prev.addresses];
      addresses[idx] = { ...addresses[idx], ...addr };
      return { ...prev, addresses };
    });
  };

  const setPrimaryAddress = (idx) => {
    setForm(prev => ({
      ...prev,
      addresses: prev.addresses.map((a, i) => ({ ...a, is_primary: i === idx })),
    }));
  };

  // PR-73 Commit 2 (dispatch 431 §3, Reading C):
  // Mirrors setPrimaryAddress exactly. Called by the job-site radio
  // when the user picks which of the >=2 addresses is the job site.
  const setJobSiteAddress = (idx) => {
    setForm(prev => ({
      ...prev,
      addresses: prev.addresses.map((a, i) => ({ ...a, is_job_site: i === idx })),
    }));
  };

  // ─── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    const displayName = form.use_company_as_display ? form.company : `${form.first_name} ${form.last_name}`.trim();
    if (!displayName) return 'Customer name is required';
    const primaryContact = form.contacts.find(c => c.is_primary) || form.contacts[0];
    if (!primaryContact?.phone && !primaryContact?.email) return 'Primary contact phone or email is required';
    if (!form.customer_type) return 'Customer type is required';
    return null;
  };

  // ─── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const displayName = form.use_company_as_display
      ? form.company
      : `${form.first_name} ${form.last_name}`.trim();

    // IDI-138: resolve the flat id array (form.salesperson, which is what
    // TypeaheadChips's value/onChange contract needs) into {id, name} pairs
    // ONCE, at the point of writing — see CustomerFormPanel.buildPayload's
    // identical fix for the full rationale.
    const salespeople = form.salesperson.map((id) => {
      const opt = salespersonOptions.find((o) => o.value === id);
      return { id, name: opt?.label || id };
    });

    const payload = {
      entityType: 'customer', // Iter-18: required so backend list filter (Go customers/main.go:97) includes this record
      name: displayName,
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      company: form.company.trim(),
      use_company_as_display: form.use_company_as_display,
      customer_type: form.customer_type,
      status: form.status,
      contacts: form.contacts
        .filter(c => c.first_name || c.last_name || c.phone || c.email)
        .map(c => ({ ...c, name: `${c.first_name || ''} ${c.last_name || ''}`.trim() })),
      // IDI-247 (2026-09-09): the canonical stored address on the customer
      // record is `serviceAddress` — the backend read path at
      // cmd/customers/main.go:373-381 DERIVES `address` from this on GET.
      // The two previous writes here (`addresses[]` and legacy `address`)
      // were either overwritten on next read (`address`) or persistent but
      // outranking `serviceAddress` in the resolver (`addresses[]`, step 5
      // vs step 8 in getJobSiteAddress). Neither is reachable today
      // (0/500 jobs need step 5+), but the latent trap goes away by writing
      // only the canonical shape. `toServiceAddressShape` also handles the
      // line2→address2 alias correctly — 23 customers had their `address2`
      // silently dropped by the prior writer. Sibling defect at
      // customerAddress.js normalise() is IDI-249 (separate ticket).
      ...(function () {
        const primary = form.addresses.find(a => a.is_primary) || form.addresses[0];
        const shape = toServiceAddressShape(primary);
        return shape ? { serviceAddress: shape } : {};
      })(),
      billing_same_as_primary: form.billing_same_as_primary,
      // PR-73 Commit 2 (dispatch 431 §3, snake-case per dispatch 428 §3):
      // the customer-level "job site same as primary" flag.
      // IDI-247 (2026-09-09): `addresses[]` is no longer emitted (see the
      // serviceAddress IIFE above). Per-address `is_job_site` bits were
      // carried on that array; they are now dropped by design per the
      // ticket's deferral ("until something owns that array").
      job_site_same_as_primary: form.job_site_same_as_primary,
      // §1 (2026-08-08): send separate billing_address only when flag is off
      // AND the user actually entered something. Empty entries → null so we don't
      // clobber a previously-saved separate address.
      billing_address: (!form.billing_same_as_primary && form.billing_address && (form.billing_address.line1 || form.billing_address.zip))
        ? form.billing_address
        : null,
      // Legacy fields for backward compat
      phone: form.contacts.find(c => c.is_primary)?.phone || form.contacts[0]?.phone || '',
      email: form.contacts.find(c => c.is_primary)?.email || form.contacts[0]?.email || '',
      payment_terms: form.payment_terms || undefined,
      payment_type: form.payment_type || undefined,
      default_margin: marginFromDisplayScale(form.default_margin),
      // IDI-138: resolve the flat id array (form.salesperson) into {id, name}
      // pairs at the boundary where it's actually written — see
      // CustomerFormPanel.buildPayload's identical fix for the full rationale.
      salesperson: salespeople[0] || undefined,
      salespersons: salespeople,
      // PR-14: only ship lead_source when status === 'lead'.
      lead_source: form.status === 'lead' ? (form.lead_source || undefined) : undefined,
    };

    try {
      setSaving(true);
      setError(null);
      if (isEdit) {
        await customerAPI.updateCustomer(editCustomer.id, payload);
        emit('customer.updated', { customer_type: form.customer_type, status: form.status });
      } else {
        await customerAPI.createCustomer(payload);
        emit('customer.created', { customer_type: form.customer_type, status: form.status });
      }
      onSaved();
    } catch (err) {
      setError(err.message || `Failed to ${isEdit ? 'update' : 'create'} customer`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/30 z-40 transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Drawer */}
      <div
        className={clsx(
          'fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-white shadow-xl transform transition-transform duration-300 ease-in-out',
          open ? 'translate-x-0' : 'translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? 'Edit Customer' : 'Add Customer'}
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[color:var(--color-border)] gap-3">
            <h2 className="text-lg font-semibold text-gray-900 flex-shrink-0">
              {isEdit ? 'Edit Customer' : 'Add Customer'}
            </h2>
            {/* Iter-18 CU-1 wire: Lead|Active status toggle in header. */}
            <div className="flex gap-1 flex-shrink-0" role="tablist" aria-label="Customer status">
              <button
                type="button"
                role="tab"
                aria-selected={form.status === 'lead'}
                onClick={() => setForm(prev => ({ ...prev, status: 'lead' }))}
                className={clsx(
                  'px-3 py-1.5 text-xs font-medium border transition-colors',
                  form.status === 'lead'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-[color:var(--color-border)] text-gray-600 hover:border-[color:var(--color-border)] bg-white'
                )}
                style={{ borderRadius: 'var(--radius-md)' }}
              >
                Lead
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={form.status === 'active'}
                onClick={() => setForm(prev => ({ ...prev, status: 'active' }))}
                className={clsx(
                  'px-3 py-1.5 text-xs font-medium border transition-colors',
                  form.status === 'active'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-[color:var(--color-border)] text-gray-600 hover:border-[color:var(--color-border)] bg-white'
                )}
                style={{ borderRadius: 'var(--radius-md)' }}
              >
                Active
              </button>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex-shrink-0" style={{ borderRadius: 'var(--radius-md)' }}
              aria-label="Close drawer"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <form id="add-customer-form" onSubmit={handleSubmit} className="space-y-5">
              {error && <Alert variant="error">{error}</Alert>}

              {/* ─── Name Section (Jobber pattern) ─── */}
              <fieldset className="space-y-3">
                <legend className="text-sm font-semibold text-gray-900">Customer Name</legend>
                <NameFields
                  firstName={form.first_name}
                  lastName={form.last_name}
                  onFirstNameChange={handleChange('first_name')}
                  onLastNameChange={handleChange('last_name')}
                  firstRef={firstInputRef}
                  idPrefix="cust"
                />
                <div>
                  <label htmlFor="cust-company" className="block text-xs font-medium text-gray-700 mb-1">
                    Company
                  </label>
                  <Input
                    id="cust-company"
                    value={form.company}
                    onChange={handleChange('company')}
                    placeholder="Company name (optional)"
                  />
                </div>
                {/* Iter-18: always render (per operator flag); disabled when no company */}
                <Checkbox
                  checked={form.use_company_as_display}
                  onChange={(checked) => setForm(prev => ({ ...prev, use_company_as_display: checked }))}
                  disabled={!form.company}
                  label="Use company name as display name"
                />
              </fieldset>

              {/* ─── Type & Lead Toggle ─── */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="cust-type" className="block text-sm font-medium text-gray-700 mb-1">
                    Customer Type <span className="text-red-500">*</span>
                  </label>
                  <SelectMenu
                    id="cust-type"
                    value={form.customer_type || null}
                    onChange={(val) => { setForm(prev => ({ ...prev, customer_type: val || '' })); setError(null); }}
                    options={typeOptions}
                    placeholder="Select type..."
                    width="full"
                    invalid={!form.customer_type && !!error}
                  />
                </div>
                {/* Iter-18 CU-1 wire: Lead|Active toggle moved to drawer header (next to close X) */}
              </div>

              {/* ─── Contacts (multi-contact) ─── */}
              <fieldset className="space-y-3">
                <legend className="text-sm font-semibold text-gray-900 flex items-center justify-between w-full">
                  <span>Contacts</span>
                  <button
                    type="button"
                    onClick={addContact}
                    className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                  >
                    <PlusIcon className="h-3 w-3" /> Add Contact
                  </button>
                </legend>
                {form.contacts.map((contact, idx) => (
                  <div key={idx} className="border border-[color:var(--color-border)] p-3 space-y-2 relative" style={{ borderRadius: 'var(--radius-md)' }}>
                    <div className="flex items-center justify-between">
                      {/* PR-64 §1.3 (R-71/R-75): the primary-contact radio only appears
                          when there is a choice to make. With one contact, is_primary is
                          auto-true (see setPrimaryContact on-add; removeContact promotes
                          index 0). Model unchanged — display-only rule. */}
                      {form.contacts.length > 1 ? (
                        <label className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="primary-contact"
                            checked={contact.is_primary}
                            onChange={() => setPrimaryContact(idx)}
                            className="text-primary-600"
                            style={{ accentColor: 'var(--color-action-primary)' }}
                          />
                          <span className="text-xs text-gray-500">
                            {contact.is_primary ? 'Primary' : 'Set as primary'}
                          </span>
                        </label>
                      ) : (
                        <span />
                      )}
                      {form.contacts.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeContact(idx)}
                          className="text-gray-400 hover:text-red-500 p-1"
                          aria-label="Remove contact"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    {contact.is_primary && (
                      <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          disabled={!form.first_name && !form.last_name}
                          checked={!!(form.first_name || form.last_name) && contact.first_name === form.first_name && contact.last_name === form.last_name}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setForm(prev => {
                                const contacts = [...prev.contacts];
                                contacts[idx] = { ...contacts[idx], first_name: prev.first_name, last_name: prev.last_name };
                                return { ...prev, contacts };
                              });
                            }
                          }}
                          className="rounded"
                          style={{ accentColor: 'var(--color-action-primary)' }}
                        />
                        Same as customer name
                      </label>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        value={contact.first_name}
                        onChange={(e) => updateContact(idx, 'first_name', e.target.value)}
                        placeholder="First name"
                        aria-label={`Contact ${idx + 1} first name`}
                      />
                      <Input
                        value={contact.last_name}
                        onChange={(e) => updateContact(idx, 'last_name', e.target.value)}
                        placeholder="Last name"
                        aria-label={`Contact ${idx + 1} last name`}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="tel"
                        value={contact.phone}
                        onChange={(e) => updateContact(idx, 'phone', formatPhoneInput(e.target.value))}
                        placeholder="Phone"
                        aria-label={`Contact ${idx + 1} phone`}
                      />
                      <Input
                        type="email"
                        value={contact.email}
                        onChange={(e) => updateContact(idx, 'email', e.target.value)}
                        placeholder="Email"
                        aria-label={`Contact ${idx + 1} email`}
                      />
                    </div>
                    <SelectMenu
                      value={contact.role || null}
                      onChange={(v) => updateContact(idx, 'role', v || '')}
                      options={contactRoleOptions}
                      placeholder="Select role…"
                      width="full"
                      aria-label={`Contact ${idx + 1} role`}
                    />
                  </div>
                ))}
              </fieldset>

              {/* ─── Addresses (multi-address) ─── */}
              <fieldset className="space-y-3">
                <legend className="text-sm font-semibold text-gray-900 flex items-center justify-between w-full">
                  <span>Addresses</span>
                  <button
                    type="button"
                    onClick={addAddress}
                    className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
                  >
                    <PlusIcon className="h-3 w-3" /> Add Address
                  </button>
                </legend>
                {form.addresses.map((addr, idx) => (
                  <div key={idx} className="border border-[color:var(--color-border)] p-3 space-y-2 relative" style={{ borderRadius: 'var(--radius-md)' }}>
                    <div className="flex items-center justify-between">
                      {/* PR-64 §1.3 (R-71/R-75): primary-address radio hidden at length ≤ 1
                          (see contact radio above for rationale). Model unchanged. */}
                      {form.addresses.length > 1 ? (
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              name="primary-address"
                              checked={addr.is_primary}
                              onChange={() => setPrimaryAddress(idx)}
                              className="text-primary-600"
                              style={{ accentColor: 'var(--color-action-primary)' }}
                            />
                            <span className="text-xs text-gray-500">
                              {addr.is_primary ? 'Primary' : 'Set as primary'}
                            </span>
                          </label>
                          {/* PR-73 Commit 2 (dispatch 431 §3, Reading C):
                              job-site selector radio. Visible ONLY when the
                              user has unchecked "job site same as primary"
                              AND there is more than one address to choose
                              between. Mirrors the primary radio's hide-at-1
                              rule exactly. Test guard:
                              AddCustomerDrawer.job-site.test.js T-uncheck-at-2
                              and T-symmetry-length-2. */}
                          {!form.job_site_same_as_primary && (
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                name="job-site-address"
                                checked={!!addr.is_job_site}
                                onChange={() => setJobSiteAddress(idx)}
                                className="text-primary-600"
                                style={{ accentColor: 'var(--color-action-primary)' }}
                              />
                              <span className="text-xs text-gray-500">
                                {addr.is_job_site ? 'Job site' : 'Set as job site'}
                              </span>
                            </label>
                          )}
                        </div>
                      ) : (
                        <span />
                      )}
                      {form.addresses.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeAddress(idx)}
                          className="text-gray-400 hover:text-red-500 p-1"
                          aria-label="Remove address"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    <AddressComposite
                      value={addr}
                      onChange={(newAddr) => updateAddress(idx, newAddr)}
                      idPrefix={`cust-addr-${idx}`}
                    />
                  </div>
                ))}
                {/* PR-73 Commit 2 (dispatch 431 §3, Reading C):
                    "Job site same as primary" checkbox.
                    - CHECKED (default): the primary address IS the job site.
                    - UNCHECK at length=1 → creates a fresh entry flagged
                      is_job_site: true (via addAddress with a preset).
                    - UNCHECK at length>=2 → reveals the selector radios
                      (see the addresses.map above).
                    - RECHECK: flips the flag back; does NOT delete the
                      entry that was created on length=1 uncheck (§1.22 —
                      the user's typed data survives). See T-symmetry-
                      length-1 in AddCustomerDrawer.job-site.test.js.

                    Provenance: R-71 and R-75 originally ruled job-site
                    lives at quote/job level ONLY. Dispatch 431 §2
                    superseded that on 2026-08-24: the customer-record
                    designation ships here alongside the job-level
                    snapshot. Stakeholders were not told about the
                    supersession — flagged 🟠 for next UAT.

                    Twin: CustomerFormPanel.js's identical checkbox.
                    Both sites edit together per PR-72's §1.22 pattern. */}
                <Checkbox
                  checked={form.job_site_same_as_primary}
                  onChange={(checked) => setForm(prev => {
                    if (checked) {
                      // Recheck: flip the flag; leave addresses untouched
                      // (§1.22: preservation over destruction). The
                      // previously-designated entry stays in the list
                      // and stays flagged is_job_site — Reading C says
                      // when the flag is TRUE, the effective job-site
                      // is the primary regardless of what is_job_site
                      // bits say.
                      return { ...prev, job_site_same_as_primary: true };
                    }
                    // Uncheck.
                    if (prev.addresses.length <= 1) {
                      // Length=1: create a fresh entry flagged is_job_site.
                      // Clear is_job_site on the existing primary.
                      const updated = prev.addresses.map(a => ({ ...a, is_job_site: false }));
                      updated.push({
                        line1: '', line2: '', city: '', state: '', zip: '',
                        is_primary: false, is_job_site: true, label: 'Job site',
                      });
                      return {
                        ...prev,
                        job_site_same_as_primary: false,
                        addresses: updated,
                      };
                    }
                    // Length>=2: reveal the selector. Clear all is_job_site
                    // bits on first uncheck so the user is forced to pick
                    // (per T-uncheck-at-2's assertion that neither is
                    // designated yet). This clear is NOT destructive of
                    // typed data — is_job_site is a designation bit, not
                    // an address value.
                    //
                    // HOWEVER, if a prior designation exists (from a
                    // previous uncheck→pick→recheck cycle, per T-symmetry-
                    // length-2), preserve it. The test asserts that
                    // designated-B survives the recheck→uncheck round-
                    // trip. Which means: only clear the bits if NO entry
                    // is currently flagged is_job_site.
                    const anyDesignated = prev.addresses.some(a => a.is_job_site);
                    const nextAddresses = anyDesignated
                      ? prev.addresses
                      : prev.addresses.map(a => ({ ...a, is_job_site: false }));
                    return {
                      ...prev,
                      job_site_same_as_primary: false,
                      addresses: nextAddresses,
                    };
                  })}
                  label="Job site same as primary"
                />
                <Checkbox
                  checked={form.billing_same_as_primary}
                  onChange={(checked) => setForm(prev => {
                    // §1 (2026-08-08): reveal shape.
                    // PR-73 Commit 1 (2026-08-23, dispatch 427 §2 + 429 §1):
                    // SYMMETRIC preservation. Re-check no longer clears
                    // billing_address. The "no orphaned save" concern is
                    // already served by the submit guard at :260, which drops
                    // billing_address when the flag is on regardless of what
                    // form state carries — so the clear-on-re-check costs the
                    // user their typed address and prevents nothing that isn't
                    // already prevented one layer down. Preserved value is
                    // form-state-only; it cannot reach the wire while the flag
                    // is on.
                    //
                    // §1.22: a guard whose stated purpose is already served
                    // elsewhere is either dead or doing something it doesn't
                    // advertise. Here the second effect was destructive: the
                    // user's typed billing address on subsequent uncheck.
                    //
                    // This code has a twin at CustomerFormPanel.js:613-625.
                    // If you edit here, the twin needs the same edit. The
                    // T-billing-symmetry test in each component's *.test.js
                    // guards this — deviating fixes it in one place and reddens
                    // the other.
                    if (checked) {
                      return { ...prev, billing_same_as_primary: true };
                    }
                    return {
                      ...prev,
                      billing_same_as_primary: false,
                      billing_address: prev.billing_address
                        || { line1: '', line2: '', city: '', state: '', zip: '' },
                    };
                  })}
                  label="Billing address same as primary"
                />
                {/* §1 (2026-08-08): reveal a full billing-address block when the
                    flag is OFF. Uses the same AddressComposite primitive as the
                    addresses list, so the ZIP-first UX and validation are consistent. */}
                {!form.billing_same_as_primary && (
                  <div className="mt-3 pl-6 border-l-2 space-y-2" style={{ borderColor: 'var(--color-border)' }}>
                    <div className="text-xs font-medium text-gray-700">Billing address</div>
                    <AddressComposite
                      value={form.billing_address || { line1: '', line2: '', city: '', state: '', zip: '' }}
                      onChange={(newAddr) => setForm(prev => ({ ...prev, billing_address: newAddr }))}
                      idPrefix="cust-billing-addr"
                    />
                  </div>
                )}
              </fieldset>

              {/* ─── Advanced fields ─── */}
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
                aria-expanded={showAdvanced}
              >
                <ChevronDownIcon className={clsx('h-4 w-4 transition-transform', showAdvanced && 'rotate-180')} />
                {showAdvanced ? 'Hide' : 'Show'} additional fields
              </button>

              {showAdvanced && (
                <div className="space-y-4 pl-2 border-l-2 border-gray-100">
                  {/* PR-14 (2026-07-22): Lead Source only relevant while status
                      === 'lead'. Once converted to active, the field hides + is
                      excluded from the payload. */}
                  {form.status === 'lead' && (
                    <div>
                      <label htmlFor="cust-lead-source" className="block text-xs font-medium text-gray-700 mb-1">
                        Lead Source
                      </label>
                      <SelectMenu
                        id="cust-lead-source"
                        value={form.lead_source || null}
                        onChange={(val) => setForm(prev => ({ ...prev, lead_source: val || '' }))}
                        options={leadSourceOptions}
                        placeholder="Select source..."
                        width="full"
                      />
                    </div>
                  )}

                  {/* Payment */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="cust-payment-terms" className="block text-xs font-medium text-gray-700 mb-1">
                        Payment Terms
                      </label>
                      <SelectMenu
                        id="cust-payment-terms"
                        value={form.payment_terms || null}
                        onChange={(val) => setForm(prev => ({ ...prev, payment_terms: val || '' }))}
                        options={paymentTermOptions}
                        placeholder="Default"
                        width="full"
                      />
                    </div>
                    <div>
                      <label htmlFor="cust-payment-type" className="block text-xs font-medium text-gray-700 mb-1">
                        Payment Type
                      </label>
                      <SelectMenu
                        id="cust-payment-type"
                        value={form.payment_type || null}
                        onChange={(val) => setForm(prev => ({ ...prev, payment_type: val || '' }))}
                        options={paymentTypeOptions}
                        placeholder="Not set"
                        width="full"
                      />
                    </div>
                  </div>

                  {/* Margin */}
                  <div>
                    <label htmlFor="cust-margin" className="block text-xs font-medium text-gray-700 mb-1">
                      Default Gross Margin %
                    </label>
                    <Input
                      id="cust-margin"
                      type="number"
                      min="0"
                      max="99"
                      step="0.5"
                      value={form.default_margin}
                      onChange={handleChange('default_margin')}
                      placeholder="e.g., 35"
                      className="w-32"
                    />
                  </div>

                  {/* Salesperson (multiselect) */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Salesperson(s)
                    </label>
                    <TypeaheadChips
                      value={form.salesperson}
                      onChange={(arr) => setForm(prev => ({ ...prev, salesperson: arr }))}
                      options={salespersonOptions}
                      placeholder="Assigned rep(s)"
                      aria-label="Salesperson"
                    />
                  </div>

                  {/* Iter-18: Office field removed — not on CU-1 wireframe. */}
                </div>
              )}
            </form>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[color:var(--color-border)] bg-gray-50">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" form="add-customer-form" disabled={saving}>
              {saving ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Customer')}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
